import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Check, X } from "lucide-react";
import { norm } from "@/lib/normalize";

const LEAVE_TYPES = ["Casual Leave", "Sick Leave", "Earned Leave", "Unpaid Leave"];

const empty = { staffId: "", leaveType: "", startDate: "", endDate: "", reason: "" };

export default function LeaveRequest() {
  const { user } = useAuth();
  const isAdmin = norm(user.roleName) === "admin";
  const [rows, setRows] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/leaves");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { toast.error("Failed to load leave requests"); }
  };

  const loadStaff = async () => {
    try { setStaffList((await api.get("/staff")).data); } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadStaff(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!form.leaveType || !form.startDate || !form.endDate) {
      toast.error("Please fill all required fields"); return;
    }
    setSaving(true);
    try {
      await api.post("/leaves", form);
      toast.success("Leave request submitted");
      setOpen(false); setForm(empty); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setSaving(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leaves/${id}/status?status=${status}`);
      toast.success(`Leave ${status.toLowerCase()}`);
      load();
    } catch { toast.error("Failed to update"); }
  };

  const statusBadge = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") return <Badge variant="outline" className="border-green-800 text-green-300 bg-[rgba(46,125,50,0.15)]">Approved</Badge>;
    if (s === "rejected") return <Badge variant="outline" className="border-red-900 text-red-300 bg-[rgba(211,47,47,0.15)]">Rejected</Badge>;
    return <Badge variant="outline" className="border-gold-subtle text-gold bg-[rgba(197,160,89,0.1)]">Pending</Badge>;
  };

  return (
    <div className="space-y-8" data-testid="leave-request-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Time Off</div>
          <h1 className="font-heading text-4xl text-white">Leave Requests</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={() => setForm(empty)} data-testid="apply-leave-btn"
              className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Apply Leave
            </button>
          </DialogTrigger>
          <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md">
            <DialogHeader><DialogTitle className="font-heading text-2xl">Apply for Leave</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Staff *</Label>
                <Select value={form.staffId} onValueChange={(val) => setForm({ ...form, staffId: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-10">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.staffName || ""} </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Leave Type *</Label>
                <Select value={form.leaveType} onValueChange={(val) => setForm({ ...form, leaveType: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-10">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEAVE_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date *</Label>
                  <Input required type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                    className="bg-emerald-elev border-gold-subtle mt-1 text-white" />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input required type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                    className="bg-emerald-elev border-gold-subtle mt-1 text-white" />
                </div>
              </div>
              <div>
                <Label>Reason</Label>
                <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" placeholder="Optional reason for leave" />
              </div>
              <button type="submit" disabled={saving}
                className="gold-button w-full h-11 rounded-md disabled:opacity-60">
                {saving ? "Submitting…" : "Submit Request"}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              {isAdmin && <TableHead className="text-emerald-muted">Staff</TableHead>}
              <TableHead className="text-emerald-muted">Leave Type</TableHead>
              <TableHead className="text-emerald-muted">Start Date</TableHead>
              <TableHead className="text-emerald-muted">End Date</TableHead>
              <TableHead className="text-emerald-muted">Reason</TableHead>
              <TableHead className="text-emerald-muted text-center">Status</TableHead>
              {isAdmin && <TableHead className="text-emerald-muted w-32 text-center">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={isAdmin ? 7 : 5} className="text-center text-emerald-muted py-8">No leave requests.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} className="border-gold-subtle" data-testid={`leave-row-${r.id}`}>
                {isAdmin && <TableCell className="text-white">{r.staffName || "—"}</TableCell>}
                <TableCell className="text-white">{r.leaveType}</TableCell>
                <TableCell className="text-emerald-muted">{r.startDate}</TableCell>
                <TableCell className="text-emerald-muted">{r.endDate}</TableCell>
                <TableCell className="text-emerald-muted text-sm">{r.reason || "—"}</TableCell>
                <TableCell className="text-center">{statusBadge(r.status)}</TableCell>
                {isAdmin && (
                  <TableCell className="text-center">
                    {(r.status || "").toLowerCase() === "pending" && (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => updateStatus(r.id, "APPROVED")}
                          className="text-green-300 hover:text-green-200 p-1" title="Approve">
                          <Check className="w-4 h-4" />
                        </button>
                        <button onClick={() => updateStatus(r.id, "REJECTED")}
                          className="text-red-300 hover:text-red-200 p-1" title="Reject">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
